import React, { useEffect, useState, useCallback } from 'react'
import { listFeedback, getFeedbackAudit, triggerMboIngestOnce, ApiError } from '../api/orchestratorClient'

const STATUS_CONFIG = {
  NEW: { label: 'New', color: 'var(--text-muted)', bg: 'var(--border)' },
  TRIAGED: { label: 'Triaged', color: 'var(--series-1)', bg: 'var(--sequential-100)' },
  AWAITING_APPROVAL: { label: 'Awaiting Approval', color: 'var(--status-warning)', bg: '#fef3c7' },
  APPROVED: { label: 'Approved', color: 'var(--status-good)', bg: '#dcfce7' },
  IN_PROGRESS: { label: 'In Progress', color: 'var(--series-1)', bg: 'var(--sequential-100)' },
  TESTING: { label: 'Testing', color: 'var(--series-4)', bg: '#fef3c7' },
  DEPLOYED: { label: 'Deployed', color: 'var(--status-good)', bg: '#dcfce7' },
  REJECTED: { label: 'Rejected', color: 'var(--text-muted)', bg: 'var(--border)' },
  ESCALATED: { label: 'Escalated', color: 'var(--status-critical)', bg: '#fee2e2' },
}

const SOURCE_CONFIG = {
  requirements: { label: 'MBO Requirement', icon: '📝' },
  issues: { label: 'MBO Issue', icon: '🐛' },
  api: { label: 'Direct Submission', icon: '⌨️' },
}

// Which essms-mbo instance a feedback item was picked up from -- two real,
// separate deployments (testers file defects in QA; real end users create
// Requirements/Issues/Objectives/Milestones in Production), each running
// its own orchestrator process against this same shared feedback DB (see
// app/services/mbo_status_poll.py's source_environment-scoped queries).
// Previously this rendered the raw source_environment string with no
// label/color distinction and no fallback when absent.
const ENVIRONMENT_CONFIG = {
  qa: { label: 'QA', color: 'var(--series-2)' },
  production: { label: 'Production', color: 'var(--series-3)' },
}

function EnvironmentBadge({ environment }) {
  const config = ENVIRONMENT_CONFIG[environment]
  const label = config?.label || 'Unknown source'
  const color = config?.color || 'var(--text-muted)'
  return (
    <span
      className="env-badge"
      style={{ color }}
      title="Which essms-mbo instance this was picked up from"
    >
      <span className="env-dot" style={{ background: color }} />
      {label}
    </span>
  )
}

// All 17 real gate names emitted by app/graph/build.py's LangGraph
// pipeline. Previously this only had 7 keys (intake/triage/plan/approval/
// review/test/deploy) -- the last 3 didn't even match any gate the graph
// actually emits (the real names are dev_test/push_and_dev_deploy/
// qa_deploy/prod_deploy), so those stages always fell back to a generic
// icon and the raw gate-name string. Direct-API items only ever reach
// intake/triage/plan/approval/reflect; MBO-sourced items route through
// the full closed-loop chain instead of approval.
const GATE_CONFIG = {
  intake: { label: 'Intake', icon: '📥', description: 'Feedback text scanned for PII/injection, delimited as untrusted data' },
  triage: { label: 'Triage', icon: '🎯', description: 'Classified and routed to the affected essms service(s)' },
  plan: { label: 'Plan', icon: '📋', description: 'Approach, files, blast radius, and effort estimated' },
  approval: { label: 'Approval', icon: '✅', description: 'Human decision: approve, edit, or reject (direct submissions only)' },
  reflect: { label: 'Reflect', icon: '🪞', description: 'Deterministic takeaway recorded to the service’s knowledge base' },
  analysis_draft: { label: 'Analysis Drafted', icon: '📝', description: 'Plan formatted and posted as an Analysis on the essms-mbo Milestone' },
  await_accept: { label: 'Awaiting Accept', icon: '⏳', description: 'Paused until a human Accepts/Denies the Requirement/Issue in essms-mbo' },
  analysis_submit: { label: 'Analysis Submitted', icon: '📨', description: 'Analysis record created in essms-mbo, awaiting approval' },
  draft_objectives: { label: 'Objective Drafted', icon: '🧩', description: 'A DEVELOPMENT Objective created under the Milestone' },
  await_objective_wip: { label: 'Awaiting Work In Progress', icon: '⏳', description: 'Paused until the Objective moves to Work In Progress in essms-mbo' },
  implement: { label: 'Implement', icon: '💻', description: 'AI coding session made real local changes and committed them' },
  dev_test: { label: 'Dev Test', icon: '🧪', description: 'The service’s own test command run against the implementation' },
  push_and_dev_deploy: { label: 'Push & Dev Deploy', icon: '🚀', description: 'Merged and pushed to prod-main; the dev environment auto-deploys (not a production push, despite the branch name)' },
  await_qa_trigger: { label: 'Awaiting QA Trigger', icon: '⏳', description: 'Paused until a human clicks Trigger QA Deploy' },
  qa_deploy: { label: 'QA Deploy', icon: '🚦', description: 'GitLab deploy-qa manual job triggered' },
  await_prod_trigger: { label: 'Awaiting Prod Trigger', icon: '⏳', description: 'Paused until a human clicks Trigger Prod Deploy' },
  prod_deploy: { label: 'Prod Deploy', icon: '🏁', description: 'GitLab deploy-prod manual job triggered -- the real production push' },
  record_test_result: { label: 'Test Result Recorded', icon: '✅', description: 'Sibling TESTING Objective created in essms-mbo with PASS/FAIL recorded' },
}

// One-off relabeling for gate detail values that would otherwise be
// confusing shown raw -- currently just DEPLOY_BRANCH, which is
// literally named "prod-main" but pushing to it auto-deploys DEV, not
// production (see push_and_dev_deploy's description above).
function formatGateDetailValue(key, value) {
  if (key === 'deploy_branch' && value === 'prod-main') {
    return 'Dev auto-deploy (prod-main)'
  }
  return typeof value === 'object' ? JSON.stringify(value) : String(value)
}

function findGate(gates, name) {
  return gates.find(g => g.gate === name)
}

export function FeedbackAudit() {
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [selectedId, setSelectedId] = useState(null)
  const [audit, setAudit] = useState(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const [sourceFilter, setSourceFilter] = useState('all')
  const [listLoading, setListLoading] = useState(true)
  const [auditLoading, setAuditLoading] = useState(false)
  const [error, setError] = useState(null)
  const [ingesting, setIngesting] = useState(false)

  const loadList = useCallback(async () => {
    setListLoading(true)
    setError(null)
    try {
      const params = {}
      if (statusFilter !== 'all') params.status = statusFilter
      if (sourceFilter !== 'all') params.source = sourceFilter
      const res = await listFeedback(params)
      setItems(res.items)
      setTotal(res.total)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not reach the orchestrator API. Is it running?')
    } finally {
      setListLoading(false)
    }
  }, [statusFilter, sourceFilter])

  useEffect(() => {
    loadList()
  }, [loadList])

  useEffect(() => {
    if (!selectedId) {
      setAudit(null)
      return
    }
    let cancelled = false
    setAuditLoading(true)
    getFeedbackAudit(selectedId)
      .then(res => { if (!cancelled) setAudit(res) })
      .catch(e => { if (!cancelled) setError(e instanceof ApiError ? e.message : 'Failed to load audit trail') })
      .finally(() => { if (!cancelled) setAuditLoading(false) })
    return () => { cancelled = true }
  }, [selectedId])

  const handleIngestNow = async () => {
    setIngesting(true)
    setError(null)
    try {
      const res = await triggerMboIngestOnce()
      await loadList()
      if (res.created_feedback_ids.length > 0) {
        setSelectedId(res.created_feedback_ids[0])
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to trigger ingestion')
    } finally {
      setIngesting(false)
    }
  }

  return (
    <div className="feedback-audit">
      <div className="audit-header">
        <div>
          <h2>Feedback Audit Trail</h2>
          <p className="audit-subtitle">
            Real pipeline data from the orchestrator — every MBO requirement/issue and direct
            submission, with the AI's step-by-step reasoning at each gate.
          </p>
        </div>
        <button className="ingest-btn" onClick={handleIngestNow} disabled={ingesting}>
          {ingesting ? 'Checking MBO…' : '🔄 Check MBO Now'}
        </button>
      </div>

      {error && (
        <div className="audit-error" role="alert">
          <span>⚠️ {error}</span>
        </div>
      )}

      <div className="audit-layout">
        <div className="audit-list-panel">
          <div className="list-filters">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="filter-select" aria-label="Filter by status">
              <option value="all">All Statuses</option>
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <option key={key} value={key}>{cfg.label}</option>
              ))}
            </select>
            <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} className="filter-select" aria-label="Filter by source">
              <option value="all">All Sources</option>
              {Object.entries(SOURCE_CONFIG).map(([key, cfg]) => (
                <option key={key} value={key}>{cfg.label}</option>
              ))}
            </select>
          </div>

          <div className="list-count">{total} item{total !== 1 ? 's' : ''}</div>

          {listLoading ? (
            <div className="list-loading">Loading…</div>
          ) : items.length === 0 ? (
            <div className="empty-state">
              <p>No feedback items yet.</p>
              <p className="empty-hint">Submit a requirement/issue in the MBO UI, then click "Check MBO Now".</p>
            </div>
          ) : (
            <ul className="feedback-list" role="list">
              {items.map(item => (
                <li key={item.feedback_id}>
                  <button
                    className={`feedback-list-item ${selectedId === item.feedback_id ? 'active' : ''}`}
                    onClick={() => setSelectedId(item.feedback_id)}
                  >
                    <div className="item-top-row">
                      <span className="item-source">
                        {SOURCE_CONFIG[item.source]?.icon || '📄'} {SOURCE_CONFIG[item.source]?.label || item.source}
                      </span>
                      <StatusChip status={item.status} />
                    </div>
                    <div className="item-text">{item.raw_feedback}</div>
                    <div className="item-meta">
                      {item.estimated_effort && (
                        <span className="item-effort">{item.estimated_effort} · {item.estimated_hours}h</span>
                      )}
                      <span className="item-time">{formatRelativeTime(item.created_at)}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="audit-detail-panel">
          {!selectedId ? (
            <div className="detail-placeholder">
              <p>Select a feedback item to see its step-by-step audit trail.</p>
            </div>
          ) : auditLoading ? (
            <div className="list-loading">Loading audit trail…</div>
          ) : audit ? (
            <AuditDetail audit={audit} />
          ) : null}
        </div>
      </div>

      <style jsx>{`
        .feedback-audit {
          display: flex;
          flex-direction: column;
          gap: 16px;
          height: 100%;
        }
        .audit-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
        }
        .audit-header h2 {
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 4px;
        }
        .audit-subtitle {
          font-size: 13px;
          color: var(--text-secondary);
          max-width: 560px;
        }
        .ingest-btn {
          padding: 8px 16px;
          border-radius: 6px;
          background: var(--series-1);
          color: white;
          font-size: 13px;
          font-weight: 500;
          white-space: nowrap;
        }
        .ingest-btn:hover:not(:disabled) {
          opacity: 0.9;
        }
        .ingest-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .audit-error {
          padding: 10px 14px;
          background: #fee2e2;
          color: #991b1b;
          border-radius: 6px;
          font-size: 13px;
        }
        @media (prefers-color-scheme: dark) {
          :root:where(:not([data-theme="light"])) .audit-error,
          [data-theme="dark"] .audit-error {
            background: #7f1d1d;
            color: #fca5a5;
          }
        }
        .audit-layout {
          display: grid;
          grid-template-columns: 360px 1fr;
          gap: 16px;
          flex: 1;
          min-height: 0;
        }
        .audit-list-panel {
          display: flex;
          flex-direction: column;
          gap: 12px;
          background: var(--surface-1);
          border: 1px solid var(--border);
          border-radius: 8px;
          padding: 16px;
          overflow: hidden;
        }
        .list-filters {
          display: flex;
          gap: 8px;
        }
        .filter-select {
          flex: 1;
          padding: 6px 8px;
          border: 1px solid var(--border);
          border-radius: 6px;
          background: var(--page-plane);
          color: var(--text-primary);
          font-size: 12px;
        }
        .list-count {
          font-size: 11px;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .list-loading {
          padding: 40px 0;
          text-align: center;
          color: var(--text-muted);
          font-size: 13px;
        }
        .empty-state {
          padding: 40px 12px;
          text-align: center;
          color: var(--text-muted);
        }
        .empty-hint {
          font-size: 12px;
          margin-top: 4px;
        }
        .feedback-list {
          list-style: none;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .feedback-list-item {
          display: flex;
          flex-direction: column;
          gap: 6px;
          width: 100%;
          text-align: left;
          padding: 10px 12px;
          border-radius: 6px;
          border: 1px solid var(--border);
          background: var(--page-plane);
        }
        .feedback-list-item:hover {
          border-color: var(--series-1);
        }
        .feedback-list-item.active {
          border-color: var(--series-1);
          background: var(--sequential-100);
        }
        @media (prefers-color-scheme: dark) {
          :root:where(:not([data-theme="light"])) .feedback-list-item.active,
          [data-theme="dark"] .feedback-list-item.active {
            background: var(--sequential-700);
          }
        }
        .item-top-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }
        .item-source {
          font-size: 11px;
          color: var(--text-secondary);
          font-weight: 500;
        }
        .item-text {
          font-size: 13px;
          color: var(--text-primary);
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          line-height: 1.4;
        }
        .item-meta {
          display: flex;
          justify-content: space-between;
          font-size: 11px;
          color: var(--text-muted);
        }
        .item-effort {
          font-weight: 600;
        }
        .audit-detail-panel {
          background: var(--surface-1);
          border: 1px solid var(--border);
          border-radius: 8px;
          padding: 20px;
          overflow-y: auto;
        }
        .detail-placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: var(--text-muted);
          font-size: 13px;
        }
        @media (max-width: 900px) {
          .audit-layout {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  )
}

function AuditDetail({ audit }) {
  return (
    <div className="audit-detail">
      <div className="detail-header">
        <div className="detail-header-top">
          <span className="detail-source">
            {SOURCE_CONFIG[audit.source]?.icon || '⌨️'} {SOURCE_CONFIG[audit.source]?.label || 'Direct Submission'}
            {audit.source_id && <code className="source-id">{audit.source_id.slice(0, 8)}</code>}
            {audit.source && <EnvironmentBadge environment={audit.source_environment} />}
          </span>
          <StatusChip status={audit.status} />
        </div>
        <p className="detail-raw-text">{audit.raw_feedback}</p>
        <div className="detail-meta-row">
          <span>Submitted {formatRelativeTime(audit.created_at)}</span>
          {audit.user_id && <span>by <code>{audit.user_id.slice(0, 8)}</code></span>}
        </div>
      </div>

      {(audit.escalated_reason || audit.rejected_reason) && (
        <div className="detail-alert">
          {audit.escalated_reason && <p>⚠️ Escalated: {audit.escalated_reason}</p>}
          {audit.rejected_reason && <p>✕ Rejected: {audit.rejected_reason}</p>}
        </div>
      )}

      {audit.source && <MovementTrail audit={audit} />}

      <h3 className="timeline-heading">Step-by-step pipeline trail</h3>
      <ol className="gate-timeline">
        {audit.gates.map((gate, i) => (
          <li key={i} className="gate-step">
            <div className="gate-marker">
              <span className="gate-icon">{GATE_CONFIG[gate.gate]?.icon || '⚙️'}</span>
              {i < audit.gates.length - 1 && <span className="gate-connector" />}
            </div>
            <div className="gate-content">
              <div className="gate-title-row">
                <span className="gate-title">{GATE_CONFIG[gate.gate]?.label || gate.gate}</span>
                <span className={`gate-passed ${gate.passed ? 'ok' : 'fail'}`}>
                  {gate.passed ? '✓ Passed' : '✕ Failed'}
                </span>
                <span className="gate-time">{formatRelativeTime(gate.recorded_at)}</span>
              </div>
              <p className="gate-description">{GATE_CONFIG[gate.gate]?.description}</p>
              {gate.retry_count > 0 && (
                <p className="gate-retry">Retried {gate.retry_count} time{gate.retry_count !== 1 ? 's' : ''}</p>
              )}
              {gate.routing_mismatch && (
                <p className="gate-mismatch">⚠️ Routing mismatch flagged (FR-9)</p>
              )}
              {Object.keys(gate.details).length > 0 && (
                <dl className="gate-details">
                  {Object.entries(gate.details).map(([key, value]) => (
                    <React.Fragment key={key}>
                      <dt>{key}</dt>
                      <dd>{formatGateDetailValue(key, value)}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              )}
            </div>
          </li>
        ))}
      </ol>

      {audit.triage && (
        <section className="detail-section">
          <h3>🎯 Triage Decision</h3>
          <div className="detail-grid">
            <div><span className="field-label">Category</span><span>{audit.triage.category}</span></div>
            <div><span className="field-label">Severity</span><span>{audit.triage.severity}</span></div>
            <div className="span-2">
              <span className="field-label">Affected Services</span>
              <div className="service-tags">
                {audit.triage.affected_services.map(s => (
                  <span key={s} className="service-tag">{s}</span>
                ))}
              </div>
            </div>
            <div className="span-2">
              <span className="field-label">AI Rationale</span>
              <p className="rationale-text">{audit.triage.rationale}</p>
            </div>
          </div>
        </section>
      )}

      {audit.plan && (
        <section className="detail-section">
          <h3>📋 Plan</h3>
          <div className="detail-grid">
            <div><span className="field-label">Effort</span><span>{audit.plan.estimated_effort} ({audit.plan.estimated_hours}h)</span></div>
            <div><span className="field-label">Services Touched</span><span>{audit.plan.services_touched.join(', ')}</span></div>
            <div className="span-2">
              <span className="field-label">Approach</span>
              <p className="rationale-text">{audit.plan.approach}</p>
            </div>
            <div className="span-2">
              <span className="field-label">Files Likely Touched</span>
              <ul className="files-list">
                {audit.plan.files_likely_touched.map(f => <li key={f}><code>{f}</code></li>)}
              </ul>
            </div>
            <div className="span-2">
              <span className="field-label">User-Facing Summary</span>
              <p className="rationale-text">{audit.plan.user_summary}</p>
            </div>
          </div>
        </section>
      )}

      {audit.approval && (
        <section className="detail-section">
          <h3>✅ Approval Decision</h3>
          <div className="detail-grid">
            <div><span className="field-label">Decision</span><span>{audit.approval.decision}</span></div>
            {audit.approval.decided_by && <div><span className="field-label">Decided By</span><span>{audit.approval.decided_by}</span></div>}
            {audit.approval.comment && (
              <div className="span-2">
                <span className="field-label">Comment</span>
                <p className="rationale-text">{audit.approval.comment}</p>
              </div>
            )}
          </div>
        </section>
      )}

      <style jsx>{`
        .audit-detail {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .detail-header {
          padding-bottom: 16px;
          border-bottom: 1px solid var(--border);
        }
        .detail-header-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 10px;
        }
        .detail-source {
          font-size: 13px;
          font-weight: 500;
          color: var(--text-secondary);
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .source-id {
          font-size: 11px;
          background: var(--page-plane);
          padding: 1px 6px;
          border-radius: 4px;
        }
        .env-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 2px 8px;
          border-radius: 999px;
          background: var(--page-plane);
          border: 1px solid var(--border);
        }
        .env-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .detail-raw-text {
          font-size: 15px;
          color: var(--text-primary);
          line-height: 1.6;
          white-space: pre-wrap;
          margin-bottom: 8px;
        }
        .detail-meta-row {
          display: flex;
          gap: 16px;
          font-size: 12px;
          color: var(--text-muted);
        }
        .detail-alert {
          padding: 12px 14px;
          background: #fee2e2;
          color: #991b1b;
          border-radius: 6px;
          font-size: 13px;
        }
        @media (prefers-color-scheme: dark) {
          :root:where(:not([data-theme="light"])) .detail-alert,
          [data-theme="dark"] .detail-alert {
            background: #7f1d1d;
            color: #fca5a5;
          }
        }
        .timeline-heading {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
        }
        .gate-timeline {
          list-style: none;
          display: flex;
          flex-direction: column;
        }
        .gate-step {
          display: flex;
          gap: 12px;
        }
        .gate-marker {
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .gate-icon {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--page-plane);
          border: 2px solid var(--series-1);
          border-radius: 50%;
          font-size: 14px;
          flex-shrink: 0;
        }
        .gate-connector {
          flex: 1;
          width: 2px;
          min-height: 24px;
          background: var(--border);
          margin: 4px 0;
        }
        .gate-content {
          flex: 1;
          padding-bottom: 20px;
        }
        .gate-title-row {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 4px;
          flex-wrap: wrap;
        }
        .gate-title {
          font-weight: 600;
          font-size: 14px;
          color: var(--text-primary);
        }
        .gate-passed {
          font-size: 11px;
          font-weight: 600;
        }
        .gate-passed.ok {
          color: var(--status-good);
        }
        .gate-passed.fail {
          color: var(--status-critical);
        }
        .gate-time {
          font-size: 11px;
          color: var(--text-muted);
          margin-left: auto;
        }
        .gate-description {
          font-size: 12px;
          color: var(--text-secondary);
          margin-bottom: 6px;
        }
        .gate-retry {
          font-size: 11px;
          color: var(--status-warning);
        }
        .gate-mismatch {
          font-size: 11px;
          color: var(--status-critical);
        }
        .gate-details {
          margin-top: 6px;
          font-size: 11px;
          display: grid;
          grid-template-columns: auto 1fr;
          gap: 2px 8px;
        }
        .gate-details dt {
          color: var(--text-muted);
        }
        .gate-details dd {
          color: var(--text-primary);
          font-family: ui-monospace, SFMono-Regular, monospace;
        }
        .detail-section {
          padding-top: 16px;
          border-top: 1px solid var(--border);
        }
        .detail-section h3 {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 12px;
        }
        .detail-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .detail-grid > div {
          display: flex;
          flex-direction: column;
          gap: 4px;
          font-size: 13px;
        }
        .span-2 {
          grid-column: span 2;
        }
        .field-label {
          font-size: 11px;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .rationale-text {
          font-size: 13px;
          color: var(--text-primary);
          line-height: 1.5;
        }
        .service-tags {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }
        .service-tag {
          padding: 2px 8px;
          background: var(--sequential-100);
          color: var(--sequential-550);
          border-radius: 999px;
          font-size: 11px;
          font-weight: 600;
        }
        @media (prefers-color-scheme: dark) {
          :root:where(:not([data-theme="light"])) .service-tag,
          [data-theme="dark"] .service-tag {
            background: var(--sequential-700);
            color: var(--sequential-250);
          }
        }
        .files-list {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .files-list code {
          font-size: 12px;
          background: var(--page-plane);
          padding: 2px 6px;
          border-radius: 4px;
        }
      `}</style>
    </div>
  )
}

// "Picked from prod → analyzed → coded locally → pushed" -- pulls
// implement/push_and_dev_deploy/qa_deploy/prod_deploy straight out of the
// same gate list the step-by-step trail below already renders, just
// summarized as a single at-a-glance movement timeline. Only rendered for
// MBO-sourced items (audit.source set) -- a direct API submission has no
// "which environment" story to tell.
function MovementTrail({ audit }) {
  const implementGate = findGate(audit.gates, 'implement')
  const pushGate = findGate(audit.gates, 'push_and_dev_deploy')
  const qaGate = findGate(audit.gates, 'qa_deploy')
  const prodGate = findGate(audit.gates, 'prod_deploy')

  const steps = [
    {
      key: 'source',
      label: 'Source',
      done: true,
      detail: audit.source_environment
        ? `Picked up from ${ENVIRONMENT_CONFIG[audit.source_environment]?.label || audit.source_environment}`
        : 'Environment not recorded',
    },
    {
      key: 'implement',
      label: 'Implement',
      done: !!implementGate,
      failed: implementGate && !implementGate.passed,
      detail: implementGate ? (
        <>
          {implementGate.details.branch_name && (
            <div>Branch: <code>{implementGate.details.branch_name}</code></div>
          )}
          {implementGate.details.commit_shas?.length > 0 && (
            <div>Commit: <code>{implementGate.details.commit_shas[0].slice(0, 8)}</code></div>
          )}
          {implementGate.details.files_changed?.length > 0 && (
            <div>{implementGate.details.files_changed.length} file(s) changed</div>
          )}
        </>
      ) : 'Not reached yet',
    },
    {
      key: 'push_and_dev_deploy',
      label: 'Dev Auto-Deploy',
      done: !!pushGate,
      detail: pushGate ? (
        <>
          <div>{formatGateDetailValue('deploy_branch', pushGate.details.deploy_branch || 'prod-main')}</div>
          {pushGate.details.merge_commit_sha && (
            <div>Merge commit: <code>{pushGate.details.merge_commit_sha.slice(0, 8)}</code></div>
          )}
        </>
      ) : 'Not reached yet',
    },
    {
      key: 'qa_deploy',
      label: 'QA Deploy',
      done: !!qaGate,
      detail: qaGate
        ? `GitLab job "${qaGate.details.job_name}" triggered${qaGate.details.service_id ? ` for ${qaGate.details.service_id}` : ''}`
        : 'Not reached yet',
    },
    {
      key: 'prod_deploy',
      label: 'Prod Deploy',
      done: !!prodGate,
      detail: prodGate
        ? `GitLab job "${prodGate.details.job_name}" triggered${prodGate.details.service_id ? ` for ${prodGate.details.service_id}` : ''}`
        : 'Not reached yet',
    },
  ]

  return (
    <section className="movement-trail">
      <h3>🛤️ Movement: Prod → Local Dev → Push</h3>
      <ol className="movement-steps">
        {steps.map(step => (
          <li key={step.key} className={`movement-step ${step.done ? (step.failed ? 'failed' : 'done') : 'pending'}`}>
            <span className="movement-dot" />
            <div>
              <div className="movement-label">{step.label}</div>
              <div className="movement-detail">{step.detail}</div>
            </div>
          </li>
        ))}
      </ol>
      <style jsx>{`
        .movement-trail {
          padding: 14px 16px;
          background: var(--page-plane);
          border: 1px solid var(--border);
          border-radius: 8px;
        }
        .movement-trail h3 {
          font-size: 13px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 10px;
        }
        .movement-steps {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .movement-step {
          display: flex;
          gap: 10px;
          align-items: flex-start;
        }
        .movement-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          margin-top: 4px;
          flex-shrink: 0;
          background: var(--border);
        }
        .movement-step.done .movement-dot {
          background: var(--status-good);
        }
        .movement-step.failed .movement-dot {
          background: var(--status-critical);
        }
        .movement-step.pending .movement-detail {
          color: var(--text-muted);
        }
        .movement-label {
          font-size: 12px;
          font-weight: 600;
          color: var(--text-primary);
        }
        .movement-detail {
          font-size: 12px;
          color: var(--text-secondary);
          line-height: 1.5;
        }
        .movement-detail code {
          font-size: 11px;
          background: var(--surface-1);
          padding: 1px 5px;
          border-radius: 4px;
        }
      `}</style>
    </section>
  )
}

function StatusChip({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.NEW
  return (
    <span className="status-chip" style={{ background: config.bg, color: config.color }}>
      <span className="status-dot" style={{ background: config.color }} />
      {config.label}
      <style jsx>{`
        .status-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 3px 10px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 600;
          white-space: nowrap;
        }
        .status-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
        }
      `}</style>
    </span>
  )
}

function formatRelativeTime(timestamp) {
  const diff = Date.now() - new Date(timestamp).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}
